import React, { useState, useEffect, useCallback } from "react";
import { useTranslation } from "react-i18next";
import UniversityManagerLayout from "../../components/layout/UniversityManagerLayout";
import StatsCard from "../../components/common/StatsCard";
import { useTenant } from "../../context/TenantContext";
import { useUniAdminAuth } from "../../context/UniAdminAuthContext";
import { PERMISSIONS } from "../../utils/permissionKeys";
import umService from "../../services/university-admin/umService";
import { Link, useLocation } from "react-router-dom";

// TODO: replace with the real feedback/support destination once available.
const SUPPORT_FEEDBACK_HREF = "https://errnify.com/contact";
// TODO: replace with the real sales/contact destination once available.
const SUPPORT_CONTACT_HREF = "https://errnify.com/";

const UMDashboard = () => {
  const { t } = useTranslation("universityManager");
  const { userRole, currentTenantId } = useTenant();
  const { can } = useUniAdminAuth();
  const location = useLocation();
  // This component is mounted under both the real /uni-admin tree and the
  // public /university-manager mock demo, and the two trees don't share leaf
  // paths 1:1 (finance/audit-log routes differ), so links must be built from
  // the current base path rather than hardcoded to one tree.
  const isUniAdmin = location.pathname.startsWith("/uni-admin");
  const basePath = isUniAdmin ? "/uni-admin" : "/university-manager";
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchStats = useCallback(async () => {
    setLoading(true);
    const res = await umService.getDashboardStats({ userRole, currentTenantId });
    if (res.success) setStats(res.data);
    setLoading(false);
  }, [userRole, currentTenantId]);

  useEffect(() => {
    fetchStats();
  }, [fetchStats]);

  // Each field is null when the backend determined the caller lacks the
  // permission behind that stat (hide the card), vs. a number -- including
  // 0 -- when they can see it (render the card). Never use truthiness here:
  // a permitted stat that happens to be 0 must still show its card.
  const cards = [
    { icon: "fas fa-building", title: t("dashboard.stats.campuses"), value: stats?.totalCampuses, iconColor: "primary" },
    { icon: "fas fa-graduation-cap", title: t("dashboard.stats.faculties"), value: stats?.totalFaculties, iconColor: "info" },
    { icon: "fas fa-sitemap", title: t("dashboard.stats.departments"), value: stats?.totalDepartments, iconColor: "secondary" },
    { icon: "fas fa-users", title: t("dashboard.stats.clubs"), value: stats?.totalClubs, iconColor: "success" },
    { icon: "fas fa-calendar-alt", title: t("dashboard.stats.events"), value: stats?.totalEvents, iconColor: "info" },
    { icon: "fas fa-clock", title: t("dashboard.stats.pendingApprovals"), value: stats?.pendingApprovals, iconColor: "warning" },
    { icon: "fas fa-user-friends", title: t("dashboard.stats.totalUsers"), value: stats?.totalUsers, iconColor: "primary" },
    { icon: "fas fa-exchange-alt", title: t("dashboard.stats.totalTransfers"), value: stats?.totalTransfers, iconColor: "success" },
  ].filter((card) => loading || (card.value !== null && card.value !== undefined));

  return (
    <UniversityManagerLayout>
      <div className="university-manager-dashboard">
        {/* Page Header */}
        <div className="d-flex flex-column flex-md-row justify-content-between align-items-start align-items-md-center mb-4 gap-3">
          <div>
            <h1 className="h3 mb-2 fw-bold text-dark">{t("dashboard.title")}</h1>
            <p className="text-muted mb-0" style={{ fontSize: "0.9rem" }}>
              {t("dashboard.subtitle")}
            </p>
          </div>
          <button
            className="btn btn-primary d-flex align-items-center"
            onClick={fetchStats}
            disabled={loading}
          >
            <i className={`fas fa-sync-alt me-2 ${loading ? "fa-spin" : ""}`}></i>
            {t("dashboard.refresh")}
          </button>
        </div>

        {/* Stats Cards Grid */}
        <div className="row g-3 g-lg-4 mb-4">
          {cards.map((card, i) => (
            <div key={i} className="col-xl-3 col-lg-4 col-md-6 col-sm-6">
              <StatsCard
                title={card.title}
                value={card.value}
                icon={card.icon}
                iconColor={card.iconColor}
                loading={loading}
              />
            </div>
          ))}
        </div>

        {/* Quick Actions Card */}
        <div className="card border-0 shadow-sm">
          <div className="card-body p-4">
            <div className="d-flex align-items-center mb-4">
              <div
                className="rounded-3 bg-primary-subtle text-primary d-flex align-items-center justify-content-center me-3"
                style={{ width: "40px", height: "40px" }}
              >
                <i className="fas fa-bolt"></i>
              </div>
              <div>
                <h5 className="mb-0 fw-bold">{t("dashboard.quickActions")}</h5>
                <small className="text-muted">{t("dashboard.quickActionsSubtitle")}</small>
              </div>
            </div>

            <div className="row g-3">
              {can(PERMISSIONS.event.list) && (
              <div className="col-lg-3 col-md-6 col-sm-6">
                <Link
                  to={`${basePath}/events/awaiting`}
                  className="btn btn-warning w-100 d-flex flex-column align-items-center justify-content-center py-3 position-relative"
                  style={{ minHeight: "100px" }}
                >
                  <i className="fas fa-clock fs-4 mb-2"></i>
                  <span className="fw-medium">{t("dashboard.pendingEvents")}</span>
                  {stats?.pendingApprovals > 0 && (
                    <span
                      className="badge bg-danger position-absolute"
                      style={{ top: "10px", right: "10px" }}
                    >
                      {stats.pendingApprovals}
                    </span>
                  )}
                </Link>
              </div>
              )}

              {can(PERMISSIONS.club.list) && (
              <div className="col-lg-3 col-md-6 col-sm-6">
                <Link
                  to={`${basePath}/clubs`}
                  className="btn btn-outline-primary w-100 d-flex flex-column align-items-center justify-content-center py-3"
                  style={{ minHeight: "100px" }}
                >
                  <i className="fas fa-users fs-4 mb-2"></i>
                  <span className="fw-medium">{t("dashboard.viewClubs")}</span>
                </Link>
              </div>
              )}

              {can(PERMISSIONS.transaction.list) && (
              <div className="col-lg-3 col-md-6 col-sm-6">
                <Link
                  to={isUniAdmin ? `${basePath}/finance` : `${basePath}/finance/transfers`}
                  className="btn btn-outline-success w-100 d-flex flex-column align-items-center justify-content-center py-3"
                  style={{ minHeight: "100px" }}
                >
                  <i className="fas fa-exchange-alt fs-4 mb-2"></i>
                  <span className="fw-medium">{t("dashboard.transfers")}</span>
                </Link>
              </div>
              )}

              {can(PERMISSIONS.auditLog.list) && (
              <div className="col-lg-3 col-md-6 col-sm-6">
                <Link
                  to={isUniAdmin ? `${basePath}/audit-log` : `${basePath}/log-records`}
                  className="btn btn-outline-info w-100 d-flex flex-column align-items-center justify-content-center py-3"
                  style={{ minHeight: "100px" }}
                >
                  <i className="fas fa-clipboard-list fs-4 mb-2"></i>
                  <span className="fw-medium">{t("dashboard.logs")}</span>
                </Link>
              </div>
              )}
            </div>
          </div>
        </div>

        {/* Support & Contact */}
        <div className="card border-0 shadow-sm mt-4">
          <div className="card-body p-4">
            <div className="row g-4">
              <div className="col-md-6">
                <div className="d-flex align-items-start">
                  <div
                    className="rounded-3 bg-secondary-subtle text-secondary d-flex align-items-center justify-content-center me-3 flex-shrink-0"
                    style={{ width: "40px", height: "40px" }}
                  >
                    <i className="fas fa-bug"></i>
                  </div>
                  <div>
                    <div className="fw-medium text-dark" style={{ fontSize: "0.9rem" }}>
                      {t("dashboard.support.bugTitle")}
                    </div>
                    <p className="text-muted mb-1" style={{ fontSize: "0.8rem" }}>
                      {t("dashboard.support.bugSubtitle")}
                    </p>
                    <a
                      href={SUPPORT_FEEDBACK_HREF}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-decoration-none fw-medium"
                      style={{ fontSize: "0.85rem" }}
                    >
                      {t("dashboard.support.bugCta")}
                      <i className="fas fa-arrow-right ms-1" style={{ fontSize: "0.7rem" }}></i>
                    </a>
                  </div>
                </div>
              </div>

              <div className="col-md-6">
                <div className="d-flex align-items-start ps-md-4">
                  <div
                    className="rounded-3 bg-secondary-subtle text-secondary d-flex align-items-center justify-content-center me-3 flex-shrink-0"
                    style={{ width: "40px", height: "40px" }}
                  >
                    <i className="fas fa-layer-group"></i>
                  </div>
                  <div>
                    <div className="fw-medium text-dark" style={{ fontSize: "0.9rem" }}>
                      {t("dashboard.support.contactTitle")}
                    </div>
                    <p className="text-muted mb-1" style={{ fontSize: "0.8rem" }}>
                      {t("dashboard.support.contactSubtitle")}
                    </p>
                    <a
                      href={SUPPORT_CONTACT_HREF}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-decoration-none fw-medium"
                      style={{ fontSize: "0.85rem" }}
                    >
                      {t("dashboard.support.contactCta")}
                      <i className="fas fa-arrow-right ms-1" style={{ fontSize: "0.7rem" }}></i>
                    </a>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </UniversityManagerLayout>
  );
};

export default UMDashboard;
